import React, { useState } from 'react';
import { masterService } from '../../../services/masterService';

const INITIAL_FORM = {
  labObservationId: '',
  name: '',
  readingFormat: 'Numeric',
  unit: '',
  roundUpto: 2,
  defaultValue: '',
  minCritical: '',
  maxCritical: '',
  resultRequired: true,
  isComment: false,
};

export const CreateObservationDrawer = ({
  isOpen,
  onClose,
  investigationId,
  investigationName,
  onCreatedAndMap,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleAutoSlug = () => {
    if (!formData.labObservationId && formData.name) {
      const slug = formData.name
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .slice(0, 16);
      handleChange('labObservationId', slug);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Parameter name is required.');
      return;
    }
    if (!formData.labObservationId.trim()) {
      setError('Parameter code is required.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        labObservationId: formData.labObservationId.trim().toUpperCase(),
        name: formData.name.trim(),
        readingFormat: formData.readingFormat,
        unit: formData.unit.trim() || null,
        roundUpto: parseInt(formData.roundUpto, 10) || 0,
        defaultValue: formData.defaultValue.trim() || null,
        minCritical: formData.minCritical.trim() || null,
        maxCritical: formData.maxCritical.trim() || null,
        resultRequired: formData.resultRequired,
        isComment: formData.isComment,
        investigationId: investigationId || null,
        printOrder: 0,
      };

      await masterService.createObservation(payload);

      // Automatically map it to the active investigation
      onCreatedAndMap({
        labObservationId: payload.labObservationId,
        observationName: payload.name,
        readingFormat: payload.readingFormat,
        unit: payload.unit,
        prefix: '',
        childFlag: false,
        isCritical: !!(payload.minCritical || payload.maxCritical),
        isamr: false,
        isreflex: false,
        isComment: payload.isComment,
        isBold: false,
        isUnderLine: false,
        sepratePrint: false,
        chkHelp: false,
        isAutoConsume: false,
        microscopy: false,
        parentId: null,
      });

      setFormData(INITIAL_FORM);
      onClose();
    } catch (err) {
      console.error('Failed to create observation', err);
      const msg = err.response?.data?.message || err.message || 'Failed to create parameter';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="iom-drawer-overlay" onClick={onClose}>
      <div className="iom-drawer-panel iom-drawer-panel-form" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="iom-drawer-header">
          <div>
            <div className="iom-drawer-kicker">IN-PLACE DEFINITION</div>
            <h3 className="iom-drawer-title">Create New Parameter</h3>
            <p className="iom-drawer-sub">
              Define a new analyte in the master directory and immediately link it to{' '}
              <strong>{investigationName || 'current investigation'}</strong>.
            </p>
          </div>
          <button type="button" className="iom-btn-close" onClick={onClose} title="Close drawer">
            ✕
          </button>
        </div>

        {error && (
          <div className="iom-alert-danger">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="iom-drawer-form-body">
          {/* Section: Basic Identity */}
          <div className="iom-form-section">
            <h4 className="iom-form-section-title">Parameter Identity</h4>
            
            <div className="iom-form-row">
              <div className="iom-form-group flex-2">
                <label className="iom-label">
                  Parameter Name <span className="req">*</span>
                </label>
                <input
                  type="text"
                  className="iom-input"
                  placeholder="e.g. Hemoglobin, Platelet Count"
                  value={formData.name}
                  onChange={e => handleChange('name', e.target.value)}
                  onBlur={handleAutoSlug}
                  required
                  autoFocus
                />
              </div>

              <div className="iom-form-group flex-1">
                <label className="iom-label">
                  Parameter Code <span className="req">*</span>
                </label>
                <input
                  type="text"
                  className="iom-input"
                  placeholder="e.g. OBS1001, HGB"
                  value={formData.labObservationId}
                  onChange={e => handleChange('labObservationId', e.target.value.toUpperCase())}
                  required
                />
              </div>
            </div>

            <div className="iom-form-row">
              <div className="iom-form-group flex-1">
                <label className="iom-label">Reading Format / Data Type</label>
                <select
                  className="iom-select"
                  value={formData.readingFormat}
                  onChange={e => handleChange('readingFormat', e.target.value)}
                >
                  <option value="Numeric">Numeric (Quantitative)</option>
                  <option value="Text">Text (Descriptive / Narrative)</option>
                  <option value="Option">Option (Pick-List Canned)</option>
                  <option value="Formula">Formula (Calculated Analyte)</option>
                </select>
              </div>

              <div className="iom-form-group flex-1">
                <label className="iom-label">Unit of Measurement</label>
                <input
                  type="text"
                  className="iom-input"
                  placeholder="e.g. g/dL, /cu.mm, %, mg/dL"
                  value={formData.unit}
                  onChange={e => handleChange('unit', e.target.value)}
                />
              </div>

              {formData.readingFormat === 'Numeric' && (
                <div className="iom-form-group flex-1">
                  <label className="iom-label">Decimal Precision</label>
                  <select
                    className="iom-select"
                    value={formData.roundUpto}
                    onChange={e => handleChange('roundUpto', e.target.value)}
                  >
                    <option value="0">0 (Integer - e.g. 150000)</option>
                    <option value="1">1 Decimal (e.g. 14.5)</option>
                    <option value="2">2 Decimals (e.g. 14.52)</option>
                    <option value="3">3 Decimals (e.g. 0.045)</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Section: Clinical & Reference Defaults */}
          <div className="iom-form-section">
            <h4 className="iom-form-section-title">Clinical Defaults & Panic Limits</h4>

            <div className="iom-form-row">
              <div className="iom-form-group flex-1">
                <label className="iom-label">Default Value (Optional)</label>
                <input
                  type="text"
                  className="iom-input"
                  placeholder="Default result or placeholder"
                  value={formData.defaultValue}
                  onChange={e => handleChange('defaultValue', e.target.value)}
                />
              </div>

              <div className="iom-form-group flex-1">
                <label className="iom-label">Panic Min (Critical)</label>
                <input
                  type="text"
                  className="iom-input"
                  placeholder="e.g. 7.0"
                  value={formData.minCritical}
                  onChange={e => handleChange('minCritical', e.target.value)}
                />
              </div>

              <div className="iom-form-group flex-1">
                <label className="iom-label">Panic Max (Critical)</label>
                <input
                  type="text"
                  className="iom-input"
                  placeholder="e.g. 20.0"
                  value={formData.maxCritical}
                  onChange={e => handleChange('maxCritical', e.target.value)}
                />
              </div>
            </div>

            <div className="iom-form-checkboxes">
              <label className="iom-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.resultRequired}
                  onChange={e => handleChange('resultRequired', e.target.checked)}
                />
                <span>Mandatory Result Entry (Cannot validate test if blank)</span>
              </label>

              <label className="iom-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isComment}
                  onChange={e => handleChange('isComment', e.target.checked)}
                />
                <span>Interpretive Comment Block (Narrative description)</span>
              </label>
            </div>
          </div>

          {/* Drawer Footer Actions */}
          <div className="iom-drawer-footer">
            <div className="iom-footer-summary">
              <span className="text-muted">Parameter will be saved to Master and linked to this test</span>
            </div>
            <div className="iom-footer-buttons">
              <button
                type="button"
                className="iom-btn iom-btn-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="iom-btn iom-btn-primary"
                disabled={saving}
              >
                {saving ? 'Creating & Linking...' : 'Create & Map Parameter'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
